import {trackTimingEvent} from 'middleware/analytics'
import {Dispatch} from 'redux'
import {fetchIpInfo} from 'services/ipInfo'
import {LocalizationConfig} from 'types/Localization'
import {
  GeolocationSource,
  getHasGeolocationPermission,
  getPosition
} from 'utils/geolocation'

import {
  getPersistedUserCity,
  persistUserCity
} from '@vio/core/src/_web/userConfig/userCity'
import {
  getPersistedUserIp,
  persistUserIp
} from '@vio/core/src/_web/userConfig/userIp'
import {Entity} from '@vio/core/src/analytics/types/Events'
import {
  getPersistedGeolocation,
  getPersistedGeolocationSource,
  persistGeolocation,
  persistGeolocationSource
} from '@vio/core/src/utils/geolocation'
import {logAndCaptureErrorMessage} from '@vio/core/src/utils/logging/errorHandlers'

interface InitialLocalizationValuesType {
  userIp?: string
  geolocation?: {lat: number; lon: number}
  geolocationSource?: GeolocationSource | string
  userCountryCode?: string
  userCountryRegion?: string
  userCity?: string
}

/**
 * When WebView, get geolocation from URL, then set source as URL.
 * Otherwise, try getting it from the persisted Cookie with persisted source.
 */
const getGeolocationFromUrlOrCookie = () => {
  return {
    geolocation: getPersistedGeolocation(),
    geolocationSource: getPersistedGeolocationSource()
  }
}

/**
 * Retrieves localization configuration using various sources in a prioritized manner.
 * It fetches localization data from URL parameters, cookies, and then, if necessary, uses a geolocation service.
 *
 * If IpInfo is down fallback to the cookie otherwise to the default country code
 * @returns Promise<LocalizationConfig> - The localization configuration based on the retrieved data.
 *
 */
export const getLocalizationConfig = async (
  dispatch: Dispatch
): Promise<
  Pick<
    LocalizationConfig,
    'userCountryRegion' | 'address' | 'geolocation' | 'userCity'
  >
> => {
  const {geolocation, geolocationSource} = getGeolocationFromUrlOrCookie()

  // Initialize localization data from persisted values (typically cookies).
  const initialLocalizationValues: InitialLocalizationValuesType = {
    userIp: getPersistedUserIp(),
    geolocation,
    geolocationSource,
    userCity: getPersistedUserCity()
  }

  // Create a copy of the initialLocalizationValues to update based on the response.
  const updatedLocalizationValues: InitialLocalizationValuesType = {
    ...initialLocalizationValues
  }

  try {
    const response = await fetchIpInfo()

    // Update the values from the IP info response only if they weren't initially set.
    updatedLocalizationValues.userIp =
      response.ip || updatedLocalizationValues.userIp

    updatedLocalizationValues.userCity =
      response.city || updatedLocalizationValues.userCity

    updatedLocalizationValues.userCountryRegion = response.region

    // If geolocation not found in the URL or Cookie, get it from ipInfo
    if (
      !initialLocalizationValues.geolocation &&
      response.lat &&
      response.lon
    ) {
      updatedLocalizationValues.geolocation = {
        lat: Number(response.lat),
        lon: Number(response.lon)
      }
      updatedLocalizationValues.geolocationSource = GeolocationSource.Ip
    }
  } catch (error) {
    logAndCaptureErrorMessage(`Error fetching IP info - ${error.message}`, {
      level: 'warning'
    })
    // If there was an error with the IP info service, attempt to get geolocation from the device.
    try {
      const hasPermission = await getHasGeolocationPermission()

      if (hasPermission) {
        const position = await getPosition()
        const {latitude, longitude} = position.coords
        updatedLocalizationValues.geolocationSource = GeolocationSource.Device
        updatedLocalizationValues.geolocation = {
          lat: Number(latitude),
          lon: Number(longitude)
        }
        trackTimingEvent(
          dispatch,
          'GeolocationCoordinates',
          'GeolocationCoordinates-start',
          Entity.GeolocationCoordinatesFetch
        )
      }
    } catch (error) {
      logAndCaptureErrorMessage(
        `Error fetching geolocation from device - ${error.message}`,
        {
          level: 'warning'
        }
      )
    }
  }

  // Persist updated values for future sessions.

  if (updatedLocalizationValues.userCity) {
    persistUserCity(updatedLocalizationValues.userCity)
  }
  if (updatedLocalizationValues.userIp)
    persistUserIp(updatedLocalizationValues.userIp)
  if (updatedLocalizationValues.geolocation)
    persistGeolocation(updatedLocalizationValues.geolocation)
  if (updatedLocalizationValues.geolocationSource)
    persistGeolocationSource(updatedLocalizationValues.geolocationSource)

  // Compile and return the full localization configuration.
  return {
    userCountryRegion: updatedLocalizationValues.userCountryRegion,
    address: updatedLocalizationValues.userIp,
    geolocation: updatedLocalizationValues.geolocation,
    userCity: updatedLocalizationValues.userCity
  }
}
